import React, {
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import BibliothequePlotly from '../BibliothequePlotly/BibliothequePlotly';
import { convertiUtsToDate } from '../GrapheXT/GrapheXT';

const EVENT_STYLES = {
  circle: { color: '#0077b6', symbol: 'circle' },
  diamond: { color: '#e07b00', symbol: 'diamond' },
};
export const DEFAULT_EVENT_STYLE = { color: '#888888', symbol: 'circle' };

// Domaines des deux zones (coordonnées paper [0-1])
const SIGNAL_DOMAIN = [0, 0.78];
const LABEL_DOMAIN = [0.84, 1.0];

const SECTION_ALPHA = '28'; // hex opacity ~16%

// ─── Helpers ──────────────────────────────────────────────────────────────────

const buildAnnotation = ({
  xDate,
  paperY,
  label,
  dateStr,
  note,
  color,
  valLine = '',
}) => ({
  x: xDate,
  xref: 'x',
  y: paperY,
  yref: 'paper',
  text:
    `<b style="font-size:13px">${label}</b>` +
    `<br><span style="color:#555">Date :</span>  ${dateStr}` +
    valLine +
    (note ? `<br><span style="color:#555">Note :</span>  ${note}` : ''),
  showarrow: true,
  arrowhead: 2,
  arrowsize: 0.8,
  arrowwidth: 2,
  arrowcolor: color,
  ax: 0,
  ay: 36,
  xanchor: 'center',
  yanchor: 'top',
  cliponaxis: false,
  align: 'left',
  font: { size: 12, color: '#1a1a2e', family: 'Montserrat, sans-serif' },
  bgcolor: 'rgba(255,255,255,0.97)',
  bordercolor: color,
  borderwidth: 1.5,
  borderpad: 10,
});

const buildEventTraces = ({
  items,
  tRaw,
  yRaw,
  yMax,
  tTotal,
  domain,
  yaxisKey,
  markerSize,
  shownLabels,
}) => {
  const sorted = [...items]
    .map((ev, i) => ({ ...ev, _i: i }))
    .sort((a, b) => a.t - b.t);

  const PROXIMITY = tTotal * 0.08;
  let groupId = 0;
  const groups = sorted.map((ev, i) => {
    if (i > 0 && Math.abs(ev.t - sorted[i - 1].t) > PROXIMITY) groupId++;
    return groupId;
  });

  const groupCount = {};
  const indexAtX = {};
  const traces = [];
  const annMeta = [];

  sorted.forEach((ev, i) => {
    const iconType = ev.iconType || ev.label;
    const style = EVENT_STYLES[iconType] || DEFAULT_EVENT_STYLE;
    const xDate = convertiUtsToDate([ev.t])[0];
    const key = ev.t;
    indexAtX[key] = indexAtX[key] || 0;
    const stackIdx = indexAtX[key];
    indexAtX[key]++;
    const markerY = 0.5 + ((stackIdx % 3) - Math.min(stackIdx, 2) / 2) * 0.3;

    let yCurve = yMax;
    if (tRaw.length > 0) {
      let closest = 0,
        minDiff = Math.abs(tRaw[0] - ev.t);
      for (let j = 1; j < tRaw.length; j++) {
        const diff = Math.abs(tRaw[j] - ev.t);
        if (diff < minDiff) {
          minDiff = diff;
          closest = j;
        }
      }
      yCurve = yRaw[closest] ?? yMax;
    }

    const gid = groups[i];
    groupCount[gid] = (groupCount[gid] || 0) + 1;

    const displayLabel = ev.label || iconType;
    const showLegend =
      !shownLabels.has(iconType) &&
      (() => {
        shownLabels.add(iconType);
        return true;
      })();

    traces.push({
      type: 'scatter',
      mode: 'markers',
      name: displayLabel,
      legendgroup: iconType,
      showlegend: showLegend,
      xaxis: 'x',
      yaxis: yaxisKey,
      x: [xDate],
      y: [markerY],
      cliponaxis: false,
      hoverinfo: 'none',
      customdata: [ev.t],
      marker: {
        size: markerSize,
        color: style.color,
        symbol: style.symbol,
        line: { color: style.color, width: 2.5 },
      },
    });

    const dateStr = new Date(ev.t * 1000).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
    const valLine = `<br><span style="color:#555">Valeur :</span>  <b>${Math.round(yCurve * 10) / 10}</b>`;

    annMeta.push({
      xDate,
      markerY,
      style,
      label: displayLabel,
      note: ev.note,
      dateStr,
      yCurveVal: yCurve,
      valLine,
      domain,
    });
  });

  return { traces, annMeta };
};

// ─── Composant ────────────────────────────────────────────────────────────────

const LabelEventGraph = ({
  donnees = [],
  configuration = {},
  donneesGraphe,
  dimensionWidth,
  dimensionHeight,
  switchAfficherModifierIntervalle,
  viewobj_json_content = { displayModeBar: true },
  geoMode = false,
  onLabelHover,
  onLabelUnhover,
  onPhaseHover,
  onPhaseUnhover,
  onPhaseClick,
  onAddLabel,
}) => {
  // Ref vers les sections courantes — accessible dans les handlers sans dépendance useMemo
  const sectionsRef = useRef([]);
  // Ref vers les timestamps bruts du signal — pour récupérer le ts exact au hover
  const tRawRef = useRef([]);

  const { allTraces, annotations, layout } = useMemo(() => {
    // --- Signal ---
    const curve = donnees[0] || {};
    const tRaw = curve.t || curve.x || [];
    tRawRef.current = tRaw; // exposé aux handlers de hover sans créer de dépendance useMemo
    const yRaw = curve.y || [];
    const xDates = convertiUtsToDate(tRaw);
    const yMax = yRaw.length > 0 ? Math.max(...yRaw) : 1;
    const yMin = yRaw.length > 0 ? Math.min(...yRaw) : 0;
    const yRange = yMax - yMin || 1;
    const tTotal = tRaw.length > 1 ? tRaw[tRaw.length - 1] - tRaw[0] : 1;

    const signalTrace = {
      ...curve,
      x: xDates,
      y: yRaw,
      yaxis: 'y',
      name: curve.name || curve.sensor_name || 'Signal',
      hovertemplate: '%{x}<br>%{y}<extra></extra>',
    };

    const markerSize = configuration.markerSize || 16;
    const shownLabels = new Set();

    // --- Labels (anciennement events) ---
    const labelsRaw = Array.isArray(configuration.labels)
      ? configuration.labels
      : Array.isArray(configuration.events)
        ? configuration.events
        : [];

    const { traces: labelTraces, annMeta: labelAnnMeta } = buildEventTraces({
      items: labelsRaw,
      tRaw,
      yRaw,
      yMax,
      tTotal,
      domain: LABEL_DOMAIN,
      yaxisKey: 'y2',
      markerSize,
      shownLabels,
    });

    // --- Sections / Phases ---
    const sectionsRaw = Array.isArray(configuration.sections)
      ? configuration.sections
      : [];
    sectionsRef.current = sectionsRaw; // mise à jour du ref pour les handlers de clic

    const sectionShapes = sectionsRaw.map((sec) => {
      const x0 = convertiUtsToDate([sec.tStart])[0];
      const x1 = convertiUtsToDate([sec.tEnd])[0];
      const color = sec.color || '#888888';
      return {
        type: 'rect',
        xref: 'x',
        yref: 'paper',
        x0,
        x1,
        y0: SIGNAL_DOMAIN[0],
        y1: SIGNAL_DOMAIN[1],
        fillcolor: color + SECTION_ALPHA,
        line: { width: 0 },
        layer: 'below',
      };
    });

    // Annotations des phases — cliquables (captureevents) avec icône crayon
    const sectionAnnotations = sectionsRaw.map((sec) => {
      const xStart = convertiUtsToDate([sec.tStart])[0];
      const color = sec.color || '#888888';
      const title = sec.label
        ? `<b>${sec.label}</b>`
        : '<i style="color:#aaa">Phase</i>';
      return {
        x: xStart,
        xref: 'x',
        xanchor: 'left',
        y: SIGNAL_DOMAIN[1],
        yref: 'paper',
        yanchor: 'top',
        text: `${title} <span style="font-size:10px">✏</span>`,
        showarrow: false,
        font: { size: 11, color, family: 'Montserrat, sans-serif' },
        bgcolor: 'rgba(255,255,255,0.88)',
        bordercolor: color,
        borderwidth: 1,
        borderpad: 4,
        captureevents: true,
      };
    });

    // --- Lignes de connexion signal → bande labels ---
    const labelLines = labelAnnMeta.map((ev) => {
      const yNorm = Math.max(0, Math.min(1, (ev.yCurveVal - yMin) / yRange));
      const y0 =
        SIGNAL_DOMAIN[0] + (SIGNAL_DOMAIN[1] - SIGNAL_DOMAIN[0]) * yNorm;
      const y1 =
        LABEL_DOMAIN[0] + (LABEL_DOMAIN[1] - LABEL_DOMAIN[0]) * ev.markerY;
      return {
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: ev.xDate,
        x1: ev.xDate,
        y0,
        y1,
        line: { color: ev.style.color, width: 1.5, dash: 'dot' },
      };
    });

    // --- Shapes fond des bandes ---
    const bandShapes = [
      {
        type: 'rect',
        xref: 'paper',
        yref: 'paper',
        x0: 0,
        x1: 1,
        y0: LABEL_DOMAIN[0] - 0.01,
        y1: LABEL_DOMAIN[1],
        fillcolor: 'rgba(240,243,255,0.6)',
        line: { width: 1, color: '#d0d5ee' },
        layer: 'below',
      },
    ];

    const shapes = [...sectionShapes, ...bandShapes, ...labelLines];

    // --- Annotations hover (labels) ---
    const annotations = labelAnnMeta.map((ev) => {
      const paperY =
        LABEL_DOMAIN[0] + (LABEL_DOMAIN[1] - LABEL_DOMAIN[0]) * ev.markerY;
      return buildAnnotation({
        xDate: ev.xDate,
        paperY,
        label: ev.label,
        dateStr: ev.dateStr,
        note: ev.note,
        color: ev.style.color,
        valLine: ev.valLine,
      });
    });

    // --- Layout ---
    const layout = {
      title: {
        text: `${configuration.title || ''}${configuration.subtitle ? '<br>' + configuration.subtitle : ''}`,
      },
      paper_bgcolor: 'white',
      plot_bgcolor: 'white',
      xaxis: {
        title: { text: configuration.xaxis?.title || 't' },
        type: 'date',
        domain: [0, 1],
        showgrid: true,
        zeroline: false,
        anchor: 'y',
      },
      yaxis: {
        title: { text: configuration.yaxis?.title || '' },
        domain: SIGNAL_DOMAIN,
        range: [yMin - yRange * 0.05, yMax + yRange * 0.08],
        showgrid: true,
        zeroline: false,
      },
      yaxis2: {
        domain: LABEL_DOMAIN,
        range: [0, 1],
        fixedrange: true,
        visible: false,
        anchor: 'x',
      },
      shapes,
      annotations: sectionAnnotations, // labels sections toujours visibles
      legend: {
        orientation: 'v',
        x: 1.02,
        y: 1,
        xanchor: 'left',
        yanchor: 'top',
        bgcolor: 'rgba(255,255,255,0.9)',
        bordercolor: '#ddd',
        borderwidth: 1,
        tracegroupgap: 8,
        font: { size: 12 },
      },
      margin: { l: 80, r: 230, t: 60, b: 60 },
    };

    // --- Marqueurs invisibles couvrant chaque phase (hover fiable) ---
    // hoveron:'fills' + mode:'none' est peu fiable ; on utilise une rangée de marqueurs
    // transparents couvrant toute la largeur de la phase, juste sous le titre annoté.
    const N_HOVER = 20; // résolution de couverture
    const yHover = yMax + yRange * 0.04; // juste sous le plafond de l'axe y

    const phaseHoverTraces = sectionsRaw.map((sec) => {
      const phaseData = {
        type: 'phase',
        tStart: sec.tStart,
        tEnd: sec.tEnd,
        label: sec.label,
        color: sec.color,
      };
      const xPoints = Array.from({ length: N_HOVER }, (_, i) => {
        const t = sec.tStart + ((sec.tEnd - sec.tStart) * i) / (N_HOVER - 1);
        return convertiUtsToDate([Math.round(t)])[0];
      });
      return {
        type: 'scatter',
        mode: 'markers',
        hoverinfo: 'none',
        showlegend: false,
        name: '',
        xaxis: 'x',
        yaxis: 'y',
        x: xPoints,
        y: Array(N_HOVER).fill(yHover),
        marker: {
          size: 18,
          color: 'rgba(0,0,0,0)',
          symbol: 'square',
          line: { width: 0 },
        },
        customdata: Array(N_HOVER).fill(phaseData),
      };
    });

    // --- Marqueurs invisibles pour hover fiable sur les labels ---
    // Colonne de marqueurs couvrant toute la hauteur de la bande label (y2)
    const N_LABEL_HOVER = 6;
    const labelHoverTraces = labelsRaw.map((ev) => {
      const xDate = convertiUtsToDate([ev.t])[0];
      return {
        type: 'scatter',
        mode: 'markers',
        hoverinfo: 'none',
        showlegend: false,
        name: '',
        xaxis: 'x',
        yaxis: 'y2',
        x: Array(N_LABEL_HOVER).fill(xDate),
        y: Array.from(
          { length: N_LABEL_HOVER },
          (_, i) => i / (N_LABEL_HOVER - 1)
        ),
        marker: {
          size: 22,
          color: 'rgba(0,0,0,0)',
          symbol: 'square',
          line: { width: 0 },
        },
        customdata: Array(N_LABEL_HOVER).fill(ev.t),
      };
    });

    return {
      allTraces: [
        signalTrace,
        ...labelTraces,
        ...phaseHoverTraces,
        ...labelHoverTraces,
      ],
      annotations,
      layout,
    };
  }, [donnees, configuration, geoMode]);

  const [hoveredIndex, setHoveredIndex] = useState(null);
  // Bouton flottant "+ Add label" au survol de la courbe signal
  const [signalHover, setSignalHover] = useState(null);
  const signalHoverTimer = useRef(null);

  useEffect(
    () => () => {
      if (signalHoverTimer.current) clearTimeout(signalHoverTimer.current);
    },
    []
  );

  const activeLayout = useMemo(() => {
    if (hoveredIndex === null) return layout;
    const ann = annotations[hoveredIndex];
    return {
      ...layout,
      annotations: [...(layout.annotations || []), ...(ann ? [ann] : [])],
    };
  }, [layout, annotations, hoveredIndex]);

  const handleHover = useCallback(
    (e) => {
      const pt = e.points?.[0];
      if (!pt) return;

      // Courbe signal (curveNumber 0) → bouton flottant "+ Add label"
      if (pt.curveNumber === 0) {
        if (signalHoverTimer.current) clearTimeout(signalHoverTimer.current);
        const ts = tRawRef.current[pt.pointNumber];
        setSignalHover({
          ts,
          yVal: pt.y,
          pointNumber: pt.pointNumber,
          clientX: e.event?.clientX,
          clientY: e.event?.clientY,
        });
        return;
      }

      // Efface le bouton flottant si on survole autre chose
      setSignalHover(null);

      if (pt.customdata !== undefined) {
        // Phase : customdata est un objet { type: 'phase', ... }
        if (pt.customdata?.type === 'phase') {
          if (onPhaseHover)
            onPhaseHover(pt.customdata, e.event?.clientX, e.event?.clientY);
          return;
        }
        // Label : customdata est le timestamp (number)
        if (onLabelHover) {
          onLabelHover(pt.customdata, e.event?.clientX, e.event?.clientY);
          return;
        }
      }

      setHoveredIndex(pt.curveNumber - 1);
    },
    [onLabelHover, onPhaseHover]
  );

  const handleUnhover = useCallback(() => {
    setHoveredIndex(null);
    // Délai pour permettre de cliquer sur le bouton flottant avant qu'il disparaisse
    signalHoverTimer.current = setTimeout(() => setSignalHover(null), 300);
    if (onLabelUnhover) onLabelUnhover();
    if (onPhaseUnhover) onPhaseUnhover();
  }, [onLabelUnhover, onPhaseUnhover]);

  // Clic sur l'annotation du titre (captureevents) — e.index = index dans layout.annotations
  const handleClickAnnotation = useCallback(
    (e) => {
      if (!onPhaseClick || e.index === undefined) return;
      const sec = sectionsRef.current[e.index];
      if (sec)
        onPhaseClick(
          {
            type: 'phase',
            tStart: sec.tStart,
            tEnd: sec.tEnd,
            label: sec.label,
            color: sec.color,
          },
          e.event?.clientX,
          e.event?.clientY
        );
    },
    [onPhaseClick]
  );

  // Clic sur la zone transparente de la phase (hoveron fills)
  const handleClick = useCallback(
    (e) => {
      const pt = e.points?.[0];
      if (!pt || pt.customdata?.type !== 'phase') return;
      if (onPhaseClick)
        onPhaseClick(pt.customdata, e.event?.clientX, e.event?.clientY);
    },
    [onPhaseClick]
  );

  return (
    <>
      <BibliothequePlotly
        data={allTraces}
        layout={activeLayout}
        donneesGraphe={donneesGraphe}
        dimensionWidth={dimensionWidth}
        dimensionHeight={dimensionHeight}
        viewobj_json_content={viewobj_json_content}
        onClick={handleClick}
        onHover={handleHover}
        onUnhover={handleUnhover}
        onClickAnnotation={handleClickAnnotation}
        ouvrirPopupModifierIntervalle={switchAfficherModifierIntervalle}
      />

      {signalHover && (
        <div
          className="leg-signal-popup"
          style={{
            left: signalHover.clientX + 14,
            top: signalHover.clientY - 18,
          }}
          onMouseEnter={() => {
            if (signalHoverTimer.current)
              clearTimeout(signalHoverTimer.current);
          }}
          onMouseLeave={() => {
            signalHoverTimer.current = setTimeout(
              () => setSignalHover(null),
              300
            );
          }}
        >
          <div className="leg-signal-row">
            <span className="leg-signal-meta">Date</span>
            <span className="leg-signal-val">
              {signalHover.ts
                ? new Date(signalHover.ts * 1000).toLocaleDateString('fr-FR', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  })
                : '—'}
            </span>
          </div>
          <div className="leg-signal-row">
            <span className="leg-signal-meta">Valeur</span>
            <span className="leg-signal-val leg-signal-val--bold">
              {signalHover.yVal !== undefined
                ? `${Math.round(signalHover.yVal * 10) / 10}`
                : '—'}
            </span>
          </div>
          {onAddLabel && (
            <button
              className="leg-btn-add-signal"
              onClick={() => {
                onAddLabel({ ts: signalHover.ts, yVal: signalHover.yVal });
                setSignalHover(null);
              }}
            >
              + Add label
            </button>
          )}
        </div>
      )}
    </>
  );
};

export default LabelEventGraph;
